/**
 * OAUTH ONBOARDING PROVIDER
 * 
 * Checks if the authenticated user has accepted terms (for OAuth users who 
 * skip the normal signup flow). Shows a modal if terms haven't been accepted.
 */

import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { OAuthOnboardingModal } from './OAuthOnboardingModal';

interface OAuthOnboardingContextType {
  needsOnboarding: boolean;
}

const OAuthOnboardingContext = createContext<OAuthOnboardingContextType>({ needsOnboarding: false });

export function useOAuthOnboarding() {
  return useContext(OAuthOnboardingContext);
}

interface OAuthOnboardingProviderProps {
  children: ReactNode;
}

export function OAuthOnboardingProvider({ children }: OAuthOnboardingProviderProps) {
  const { user, isLoading } = useAuth();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);

  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (isLoading || !user) {
        setShowOnboarding(false);
        setHasChecked(true);
        return;
      }

      // Check if user signed up via OAuth (Google) by checking if they have a provider
      const isOAuthUser = user.app_metadata?.provider !== 'email' && 
                          user.app_metadata?.providers?.some((p: string) => p !== 'email');

      if (!isOAuthUser) {
        // Email/password users went through normal signup with terms checkbox
        setShowOnboarding(false);
        setHasChecked(true);
        return;
      }

      // Check if OAuth user has accepted terms
      const termsAccepted = user.user_metadata?.terms_accepted;
      
      if (!termsAccepted) {
        setShowOnboarding(true);
      } else {
        setShowOnboarding(false);
      }
      
      setHasChecked(true);
    };

    checkOnboardingStatus();
  }, [user, isLoading]);

  const handleComplete = async () => {
    // Refresh user metadata
    await supabase.auth.refreshSession();
    setShowOnboarding(false);
  };

  // Show a minimal loading state only while auth is actively loading
  // Once auth finishes loading (isLoading = false), we render children immediately
  // The hasChecked flag prevents a flash of the onboarding modal before we verify if needed

  return (
    <OAuthOnboardingContext.Provider value={{ needsOnboarding: showOnboarding }}>
      {children}
      {showOnboarding && user && (
        <OAuthOnboardingModal 
          isOpen={showOnboarding}
          onComplete={handleComplete}
          userId={user.id}
        />
      )}
    </OAuthOnboardingContext.Provider>
  );
}
